import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { reportsApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

type Tab = 'sales-daily' | 'sales-by-cashier' | 'sales-by-category' | 'top-customers' | 'basket'
  | 'inv-valuation' | 'inv-aging' | 'inv-turnover' | 'inv-abc';

const TABS: { key: Tab; label: string; group: 'Sales' | 'Inventory' }[] = [
  { key: 'sales-daily',       label: 'Daily sales',     group: 'Sales' },
  { key: 'sales-by-cashier',  label: 'By cashier',      group: 'Sales' },
  { key: 'sales-by-category', label: 'By category',     group: 'Sales' },
  { key: 'top-customers',     label: 'Top customers',   group: 'Sales' },
  { key: 'basket',            label: 'Basket analysis', group: 'Sales' },
  { key: 'inv-valuation',     label: 'Valuation',       group: 'Inventory' },
  { key: 'inv-aging',         label: 'Aging',           group: 'Inventory' },
  { key: 'inv-turnover',      label: 'Turnover',        group: 'Inventory' },
  { key: 'inv-abc',           label: 'ABC analysis',    group: 'Inventory' },
];

const SALESINV_TYPE_MAP: Record<string, string> = {
  'sales-daily': 'sales.daily',
  'sales-by-cashier': 'sales.by-cashier',
  'sales-by-category': 'sales.by-category',
  'top-customers': 'sales.top-customers',
  'basket': 'sales.market-basket',
  'inv-valuation': 'inventory.valuation',
  'inv-aging': 'inventory.aging',
  'inv-turnover': 'inventory.turnover',
  'inv-abc': 'inventory.abc',
};

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
}

/** FR-REP-01 / 02 — Sales + Inventory reports */
export function SalesInventoryReportsPage() {
  const initialTab = (new URLSearchParams(window.location.search).get('tab') ?? '') as Tab;
  const [tab, setTab] = useState<Tab>(
    TABS.some((t) => t.key === initialTab) ? initialTab : 'sales-daily'
  );
  const def = defaultRange();
  const [startDate, setStartDate] = useState(def.start);
  const [endDate, setEndDate] = useState(def.end);

  const grouped: Record<'Sales' | 'Inventory', typeof TABS> = { Sales: [], Inventory: [] };
  TABS.forEach((t) => grouped[t.group].push(t));
  const activeGroup = TABS.find((t) => t.key === tab)?.group ?? 'Sales';

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow={`Reports · ${activeGroup}`}
        title="Sales & inventory reports"
        subtitle="POS sales analytics and inventory analysis across a configurable date range."
      />

      {/* Date range control card */}
      <div className="card p-4 mb-6 flex gap-4 items-end flex-wrap">
        <div>
          <label className="label">Start date</label>
          <input type="date" className="input w-44" value={startDate}
            onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div>
          <label className="label">End date</label>
          <input type="date" className="input w-44" value={endDate}
            onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <div className="flex-1" />
        <ReportExportMenu
          reportType={SALESINV_TYPE_MAP[tab]}
          params={{ startDate, endDate }}
        />
      </div>

      {/* Grouped pill tabs */}
      <div className="mb-6 flex gap-2 flex-wrap items-center">
        {(['Sales', 'Inventory'] as const).map((group) => (
          <div key={group} className="flex items-center gap-1.5">
            <div className="eyebrow text-ink-400">{group}</div>
            <div className="card p-1 inline-flex gap-1">
              {grouped[group].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={clsx(
                    'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
                    tab === t.key
                      ? 'bg-canvas-subtle text-ink-900'
                      : 'text-ink-600 hover:bg-canvas-subtle'
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {tab === 'sales-daily' && <DailySalesReport startDate={startDate} endDate={endDate} />}
      {tab === 'sales-by-cashier' && <ByCashierReport startDate={startDate} endDate={endDate} />}
      {tab === 'sales-by-category' && <ByCategoryReport startDate={startDate} endDate={endDate} />}
      {tab === 'top-customers' && <TopCustomersReport startDate={startDate} endDate={endDate} />}
      {tab === 'basket' && <BasketAnalysisReport startDate={startDate} endDate={endDate} />}
      {tab === 'inv-valuation' && <ValuationReport />}
      {tab === 'inv-aging' && <AgingReport />}
      {tab === 'inv-turnover' && <TurnoverReport startDate={startDate} endDate={endDate} />}
      {tab === 'inv-abc' && <AbcReport startDate={startDate} endDate={endDate} />}
    </div>
  );
}

// ============================================================
// Helpers
// ============================================================
function fmtInt(n: number) { return n.toLocaleString(); }

function ReportHeader({ title, period }: { title: string; period?: string }) {
  return (
    <div className="mb-5">
      <div className="eyebrow text-ink-500 mb-0.5">Report</div>
      <h2 className="font-display font-bold text-2xl text-ink-900 tracking-tight">{title}</h2>
      {period && (
        <div className="font-mono text-sm text-ink-500 mt-1 tabular-nums">{period}</div>
      )}
    </div>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: string | number;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-2xl font-bold font-display text-ink-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function formatPeriod(startDate: string, endDate: string) {
  const s = new Date(startDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const e = new Date(endDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${s} → ${e}`;
}

// ============================================================
// Daily sales
// ============================================================
function DailySalesReport({ startDate, endDate }: { startDate: string; endDate: string }) {
  const q = useQuery({
    queryKey: ['rep-daily', startDate, endDate],
    queryFn: () => reportsApi.salesDaily({ startDate, endDate }),
  });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div>
      <ReportHeader title="Daily sales" period={formatPeriod(startDate, endDate)} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KpiTile label="Total sales" value={fmtInt(d.summary.sales)} />
        <KpiTile label="Items sold" value={fmtInt(Math.round(d.summary.items))} />
        <KpiTile label="Revenue" value={fmtMoney(d.summary.revenue)} accent="sage" />
      </div>

      {/* Tender breakdown */}
      {d.tenders?.length > 0 && (
        <div className="card p-5 mb-6 max-w-2xl">
          <div className="eyebrow text-ink-500 mb-3">Tender breakdown</div>
          <div className="rounded-lg border border-ink-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-canvas-subtle border-b border-ink-100">
                  <th className="px-3 py-2 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Method</th>
                  <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Count</th>
                  <th className="px-3 py-2 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {d.tenders.map((t: any) => (
                  <tr key={t.method}>
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-ink-900 uppercase">{t.method}</td>
                    <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtInt(t.count)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-ink-900 tabular-nums">{fmtMoney(t.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Daily breakdown */}
      <div className="card overflow-hidden">
        <div className="px-5 py-3 border-b border-ink-100">
          <div className="eyebrow text-ink-500">Daily breakdown</div>
        </div>
        <div className="overflow-x-auto">
          {!d.daily.length ? <EmptyState message="No sales in range." /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-canvas-subtle border-b border-ink-100">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Day</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Sales</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Items</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Subtotal</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Discount</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Tax</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {d.daily.map((r: any, i: number) => (
                  <tr key={r.day} className={i % 2 ? 'bg-canvas-subtle/40' : 'bg-canvas-elevated'}>
                    <td className="px-4 py-2 font-mono text-xs text-ink-700 tabular-nums">{r.day}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(r.saleCount)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(Math.round(r.itemsSold))}</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">{fmtMoney(r.subtotal)}</td>
                    <td className="px-4 py-2 text-right font-mono text-ink-500 tabular-nums">{fmtMoney(r.discount)}</td>
                    <td className="px-4 py-2 text-right font-mono text-ink-500 tabular-nums">{fmtMoney(r.tax)}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold text-ink-900 tabular-nums">{fmtMoney(r.grandTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// By cashier
// ============================================================
function ByCashierReport({ startDate, endDate }: { startDate: string; endDate: string }) {
  const q = useQuery({
    queryKey: ['rep-cashier', startDate, endDate],
    queryFn: () => reportsApi.salesByCashier({ startDate, endDate }),
  });
  if (q.isLoading) return <div className="card"><LoadingState rows={4} /></div>;
  if (q.error) return <ErrorState error={q.error} />;

  return (
    <div>
      <ReportHeader title="Sales by cashier" period={formatPeriod(startDate, endDate)} />
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {!q.data.rows.length ? <EmptyState message="No sales in range." /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-canvas-subtle border-b border-ink-100">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Cashier</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Sales</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Items</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Revenue</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Avg sale</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {(q.data?.rows ?? []).map((r: any, i: number) => (
                  <tr key={i} className={i % 2 ? 'bg-canvas-subtle/40' : 'bg-canvas-elevated'}>
                    <td className="px-4 py-2 font-medium text-ink-900">{r.cashierName}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(r.saleCount)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(Math.round(r.totalItems))}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold tabular-nums">{fmtMoney(r.revenue)}</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">{fmtMoney(r.avgSale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// By category
// ============================================================
function ByCategoryReport({ startDate, endDate }: { startDate: string; endDate: string }) {
  const q = useQuery({
    queryKey: ['rep-cat', startDate, endDate],
    queryFn: () => reportsApi.salesByCategory({ startDate, endDate }),
  });
  if (q.isLoading) return <div className="card"><LoadingState rows={4} /></div>;
  if (q.error) return <ErrorState error={q.error} />;

  return (
    <div>
      <ReportHeader title="Sales by category" period={formatPeriod(startDate, endDate)} />
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {!q.data.rows.length ? <EmptyState message="No sales in range." /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-canvas-subtle border-b border-ink-100">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Category</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Units</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Lines</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {(q.data?.rows ?? []).map((r: any, i: number) => (
                  <tr key={i} className={i % 2 ? 'bg-canvas-subtle/40' : 'bg-canvas-elevated'}>
                    <td className="px-4 py-2 font-medium text-ink-900">{r.categoryName}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(Math.round(r.unitsSold))}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(r.lineCount)}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold tabular-nums">{fmtMoney(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Top customers
// ============================================================
function TopCustomersReport({ startDate, endDate }: { startDate: string; endDate: string }) {
  const q = useQuery({
    queryKey: ['rep-top-cust', startDate, endDate],
    queryFn: () => reportsApi.topCustomers({ startDate, endDate, limit: 100 }),
  });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;

  return (
    <div>
      <ReportHeader title="Top customers" period={formatPeriod(startDate, endDate)} />
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {!q.data.rows.length ? <EmptyState message="No customer sales in range." /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-canvas-subtle border-b border-ink-100">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Customer</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Orders</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Total spent</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Avg order</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Last order</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {(q.data?.rows ?? []).map((r: any, i: number) => (
                  <tr key={i} className={i % 2 ? 'bg-canvas-subtle/40' : 'bg-canvas-elevated'}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-ink-900">{r.customerName}</div>
                      {r.customerEmail && (
                        <div className="text-2xs text-ink-500 mt-0.5 font-mono">{r.customerEmail}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(r.orderCount)}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold tabular-nums">{fmtMoney(r.totalSpent)}</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">{fmtMoney(r.avgOrder)}</td>
                    <td className="px-4 py-2 text-2xs text-ink-500 font-mono tabular-nums">
                      {new Date(r.lastOrder).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Basket analysis
// ============================================================
function BasketAnalysisReport({ startDate, endDate }: { startDate: string; endDate: string }) {
  const q = useQuery({
    queryKey: ['rep-basket', startDate, endDate],
    queryFn: () => reportsApi.basketAnalysis({ startDate, endDate, minCoOccurrence: 2 }),
  });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div>
      <ReportHeader title="Basket analysis" period={formatPeriod(startDate, endDate)} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <KpiTile label="Baskets analyzed" value={fmtInt(d.totalBaskets)} />
        <KpiTile label="Min co-occurrence" value={d.minCoOccurrence} />
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {!d.pairs.length ? <EmptyState message="No pairs with enough co-occurrence in range." /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-canvas-subtle border-b border-ink-100">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Product A</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Product B</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Together</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Support</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {d.pairs.map((p: any, i: number) => (
                  <tr key={i} className={i % 2 ? 'bg-canvas-subtle/40' : 'bg-canvas-elevated'}>
                    <td className="px-4 py-2">
                      <div className="font-medium text-ink-900">{p.productA.name}</div>
                      <div className="text-2xs text-ink-500 mt-0.5 font-mono">{p.productA.sku}</div>
                    </td>
                    <td className="px-4 py-2">
                      <div className="font-medium text-ink-900">{p.productB.name}</div>
                      <div className="text-2xs text-ink-500 mt-0.5 font-mono">{p.productB.sku}</div>
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-semibold tabular-nums">{fmtInt(p.coOccurrence)}</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">{p.supportPercent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Inventory valuation
// ============================================================
function ValuationReport() {
  const q = useQuery({ queryKey: ['rep-valuation'], queryFn: () => reportsApi.inventoryValuation() });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div>
      <ReportHeader title="Inventory valuation" period="As of today" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KpiTile label="Products" value={fmtInt(d.summary.productCount)} />
        <KpiTile label="Total units" value={fmtInt(Math.round(d.summary.totalUnits))} />
        <KpiTile label="Total value" value={fmtMoney(d.summary.totalValue)} accent="sage" />
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {!d.rows.length ? <EmptyState message="No stock on hand." /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-canvas-subtle border-b border-ink-100">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">SKU</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Product</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Category</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Qty</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Cost</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {d.rows.map((r: any, i: number) => (
                  <tr key={r.productId} className={i % 2 ? 'bg-canvas-subtle/40' : 'bg-canvas-elevated'}>
                    <td className="px-4 py-2 font-mono text-xs font-semibold text-ink-900">{r.sku}</td>
                    <td className="px-4 py-2 text-sm text-ink-900">{r.name}</td>
                    <td className="px-4 py-2 text-2xs text-ink-500">{r.categoryName}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(Math.round(r.totalQty))}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtMoney(r.costPrice)}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold text-ink-900 tabular-nums">{fmtMoney(r.totalValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Inventory aging
// ============================================================
function AgingReport() {
  const q = useQuery({ queryKey: ['rep-aging'], queryFn: () => reportsApi.inventoryAging() });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  const bucketTone = (label: string) => {
    if (label.includes('0-30') || label.includes('0–30')) return 'sage';
    if (label.includes('31-60') || label.includes('31–60')) return 'default';
    if (label.includes('61-90') || label.includes('61–90')) return 'amber';
    return 'danger';
  };

  return (
    <div>
      <ReportHeader title="Inventory aging" period="As of today" />

      {/* Bucket summary */}
      <div className="card p-5 mb-6">
        <div className="eyebrow text-ink-500 mb-3">Aging buckets</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {d.bucketSummary.map((b: any) => {
            const tone = bucketTone(b.label);
            const bar = {
              default: 'bg-ink-200',
              sage: 'bg-sage-500',
              amber: 'bg-amber-500',
              danger: 'bg-danger',
            }[tone];
            return (
              <div key={b.label} className="relative bg-canvas-subtle rounded-xl p-3 overflow-hidden">
                <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
                <div className="eyebrow text-ink-500 text-2xs">{b.label}</div>
                <div className="mt-2 font-display font-bold text-xl text-ink-900 tabular-nums">{fmtInt(b.count)}</div>
                <div className="text-2xs font-mono text-ink-500 mt-0.5 tabular-nums">{fmtMoney(b.value)}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {!d.rows.length ? <EmptyState message="No stock on hand." /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-canvas-subtle border-b border-ink-100">
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">SKU</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Product</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Qty</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Value</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Last movement</th>
                  <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Days</th>
                  <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Bucket</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {d.rows.map((r: any, i: number) => (
                  <tr key={r.productId} className={i % 2 ? 'bg-canvas-subtle/40' : 'bg-canvas-elevated'}>
                    <td className="px-4 py-2 font-mono text-xs font-semibold text-ink-900">{r.sku}</td>
                    <td className="px-4 py-2 text-sm text-ink-900">{r.name}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(Math.round(r.qty))}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtMoney(r.costValue)}</td>
                    <td className="px-4 py-2 text-2xs text-ink-500 font-mono tabular-nums">
                      {r.lastMovement ? new Date(r.lastMovement).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : <span className="text-ink-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{r.daysSince ?? '∞'}</td>
                    <td className="px-4 py-2 text-2xs text-ink-700 font-mono">{r.bucket}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Inventory turnover
// ============================================================
function TurnoverReport({ startDate, endDate }: { startDate: string; endDate: string }) {
  const q = useQuery({
    queryKey: ['rep-turnover', startDate, endDate],
    queryFn: () => reportsApi.inventoryTurnover({ startDate, endDate }),
  });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div>
      <ReportHeader title="Inventory turnover" period={formatPeriod(startDate, endDate)} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiTile label="COGS" value={fmtMoney(d.cogs)} />
        <KpiTile label="Avg inventory" value={fmtMoney(d.avgInventoryValue)} />
        <KpiTile label="Turnover ratio" value={d.turnoverRatio.toString()} accent="sage" />
        <KpiTile label="Days sales inv." value={d.daysSalesInventory?.toString() ?? '—'} />
      </div>

      <div className="card p-6">
        <div className="eyebrow text-ink-500 mb-3">Methodology</div>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between py-2 border-b border-ink-100">
            <dt className="text-ink-700">Beginning inventory</dt>
            <dd className="font-mono font-semibold text-ink-900 tabular-nums">{fmtMoney(d.beginningInventoryValue)}</dd>
          </div>
          <div className="flex justify-between py-2 border-b border-ink-100">
            <dt className="text-ink-700">Ending inventory</dt>
            <dd className="font-mono font-semibold text-ink-900 tabular-nums">{fmtMoney(d.endingInventoryValue)}</dd>
          </div>
          <div className="flex justify-between py-2">
            <dt className="text-ink-700">Range</dt>
            <dd className="font-mono text-ink-700 tabular-nums">
              {d.range.startDate} → {d.range.endDate}
              <span className="text-ink-400 ml-2">({d.range.rangeDays} days)</span>
            </dd>
          </div>
        </dl>
        <p className="text-xs text-ink-500 italic mt-4 pt-4 border-t border-ink-100 leading-relaxed">{d.note}</p>
      </div>
    </div>
  );
}

// ============================================================
// ABC analysis
// ============================================================
function AbcReport({ startDate, endDate }: { startDate: string; endDate: string }) {
  const q = useQuery({
    queryKey: ['rep-abc', startDate, endDate],
    queryFn: () => reportsApi.inventoryAbcAnalysis({ startDate, endDate }),
  });
  if (q.isLoading) return <div className="card"><LoadingState rows={5} /></div>;
  if (q.error) return <ErrorState error={q.error} />;
  const d = q.data;

  return (
    <div>
      <ReportHeader title="ABC analysis" period={formatPeriod(startDate, endDate)} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <KpiTile label="A items (80% revenue)" value={fmtInt(d.summary.A)} accent="sage" />
        <KpiTile label="B items (next 15%)" value={fmtInt(d.summary.B)} accent="amber" />
        <KpiTile label="C items (last 5%)" value={fmtInt(d.summary.C)} />
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          {!d.rows.length ? <EmptyState message="No sales data to classify." /> : (
            <>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-canvas-subtle border-b border-ink-100">
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Class</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">SKU</th>
                    <th className="px-4 py-2.5 text-left text-2xs font-semibold uppercase tracking-wider text-ink-500">Product</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Units</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Revenue</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">% of total</th>
                    <th className="px-4 py-2.5 text-right text-2xs font-semibold uppercase tracking-wider text-ink-500">Cumulative %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {d.rows.slice(0, 200).map((r: any, i: number) => (
                    <tr key={r.productId} className={i % 2 ? 'bg-canvas-subtle/40' : 'bg-canvas-elevated'}>
                      <td className="px-4 py-2"><AbcClassBadge classification={r.classification} /></td>
                      <td className="px-4 py-2 font-mono text-xs font-semibold text-ink-900">{r.sku}</td>
                      <td className="px-4 py-2 text-sm text-ink-900">{r.name}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{fmtInt(Math.round(r.unitsSold))}</td>
                      <td className="px-4 py-2 text-right font-mono font-semibold tabular-nums">{fmtMoney(r.revenue)}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{r.pctOfTotal}%</td>
                      <td className="px-4 py-2 text-right font-mono text-xs tabular-nums">{r.cumulativePct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {d.rows.length > 200 && (
                <div className="px-4 py-2 text-2xs text-ink-500 bg-canvas-subtle border-t border-ink-100">
                  Showing first 200 of {d.rows.length} products.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AbcClassBadge({ classification }: { classification: string }) {
  const cls =
    classification === 'A' ? 'bg-sage-50 text-sage-700 ring-sage-200' :
    classification === 'B' ? 'bg-amber-50 text-amber-800 ring-amber-200' :
                             'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={clsx(
      'inline-flex items-center justify-center font-bold text-2xs px-2 py-0.5 rounded ring-1 ring-inset w-6',
      cls
    )}>
      {classification}
    </span>
  );
}

// Shared export dropdown for operational reports (Excel / PDF / CSV).
export function ReportExportMenu({ reportType, params }: {
  reportType: string;
  params: Record<string, any>;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const run = async (format: 'xlsx' | 'pdf' | 'csv') => {
    setBusy(format); setOpen(false);
    try {
      await reportsApi.downloadExport(format, reportType, params);
    } catch {
      alert('Export failed. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={!!busy}
        className="px-4 py-2 text-sm font-semibold rounded-lg inline-flex items-center gap-2 bg-finance-700 text-white hover:bg-finance-800 shadow-sm transition-all disabled:opacity-60"
        title="Export this report"
      >
        {busy ? 'Exporting…' : 'Export'}
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M5.5 7.5L10 12l4.5-4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-44 bg-white rounded-lg shadow-lg ring-1 ring-slate-200 z-20 overflow-hidden">
            {[
              { f: 'pdf' as const, label: 'PDF document', icon: '📄' },
              { f: 'xlsx' as const, label: 'Excel workbook', icon: '📊' },
              { f: 'csv' as const, label: 'CSV (raw data)', icon: '📋' },
            ].map((o) => (
              <button key={o.f} onClick={() => run(o.f)}
                className="w-full px-4 py-2.5 text-sm text-left text-slate-700 hover:bg-canvas-subtle flex items-center gap-2.5 transition-colors">
                <span>{o.icon}</span>{o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
